import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaEye, FaTrash, FaExternalLinkAlt } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../components/TablePagination";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";
import SocialPlatformIcon, { getSocialPlatformConfig } from "../components/SocialPlatformIcon";
import { SOCIAL_PLATFORMS, socialPlatformLabel } from "../constants/socialPlatforms";
import { SupportSortableHeader } from "../pages/SupportSortableHeader";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";
import PortalAlert from "./PortalAlert";
import PortalConfirmModal from "./PortalConfirmModal";
import PortalFormPage from "./PortalFormPage";
import PortalResultsHead from "./PortalResultsHead";
import PortalFiltersCollapsible, { usePortalCompactFilters } from "./PortalFiltersCollapsible";
import { canDeleteInPortal } from "./portalSession";
import "../assets/css/DigixLeads.css";
import PortalSelect from "./PortalSelect";

const PORTAL_SOCIAL_ROWS_PER_PAGE_KEY = "portalSocialAccountsRowsPerPage";
const PORTAL_SOCIAL_CURRENT_PAGE_KEY = "portalSocialAccountsCurrentPage";

const ACCOUNT_STATUSES = ["Active", "Inactive"];
const EMPTY_FORM = {
  platformName: "",
  accountName: "",
  username: "",
  password: "",
  profileUrl: "",
  businessUrl: "",
  emailUsed: "",
  contactNumber: "",
  status: "Active",
  notes: "",
};

function display(value) {
  if (value == null || String(value).trim() === "") return "—";
  return String(value);
}

function linkOrDash(url) {
  if (!url || String(url).trim() === "") return "—";
  const href = String(url).trim();
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="cportal-link">
      {href}
    </a>
  );
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "cportal-badge cportal-badge--success";
  if (s === "inactive") return "cportal-badge cportal-badge--danger";
  return "cportal-badge";
}

const SORT_ACCESSORS = {
  accountName: (a) => (a.Account_Name || "").toLowerCase(),
  platform: (a) => (a.Platform_Name || "").toLowerCase(),
  username: (a) => (a.Username || "").toLowerCase(),
  profileUrl: (a) => (a.Profile_URL || "").toLowerCase(),
  emailUsed: (a) => (a.Email_Used || "").toLowerCase(),
  contact: (a) => a.Contact_Number || "",
  status: (a) => (a.Status || "").toLowerCase(),
};

const COLUMNS = [
  { key: "accountName", label: "Account name" },
  { key: "platform", label: "Platform" },
  { key: "username", label: "Username" },
  { key: "profileUrl", label: "Profile URL" },
  { key: "emailUsed", label: "Email used" },
  { key: "contact", label: "Contact" },
  { key: "status", label: "Status" },
];

function TenantSocialAccounts({ createMode = false }) {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState("accountName");
  const [sortDir, setSortDir] = useState("asc");
  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const saved = localStorage.getItem(PORTAL_SOCIAL_CURRENT_PAGE_KEY);
      if (!saved) return 1;
      const parsed = parseInt(saved, 10);
      return parsed > 0 ? parsed : 1;
    } catch {
      return 1;
    }
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(PORTAL_SOCIAL_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) return parsed;
    } catch {
      /* ignore */
    }
    return 10;
  });
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [search, setSearch] = useState("");
  const [resultKeyword, setResultKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const { filtersOpen, setFiltersOpen, isCompactViewport } = usePortalCompactFilters();
  const canDelete = useMemo(() => canDeleteInPortal(), []);

  const toggleSort = (key) => {
    setCurrentPage(1);
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setCurrentPage(1);
    localStorage.setItem(PORTAL_SOCIAL_ROWS_PER_PAGE_KEY, String(next));
  };

  const loadAccounts = useCallback(async () => {
    const res = await axiosInstance.get("/client/social-accounts");
    setAccounts(Array.isArray(res.data?.data) ? res.data.data : []);
  }, []);

  useEffect(() => {
    if (createMode) return undefined;
    let active = true;
    (async () => {
      try {
        await loadAccounts();
      } catch (_) {
        if (active) setError("Could not load social accounts.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [loadAccounts, createMode]);

  const platformOptions = useMemo(() => {
    const fromData = Array.from(
      new Set(accounts.map((a) => a.Platform_Name).filter(Boolean))
    );
    const known = SOCIAL_PLATFORMS.map((p) => p.value);
    return Array.from(new Set([...known, ...fromData])).sort((a, b) =>
      socialPlatformLabel(a).localeCompare(socialPlatformLabel(b))
    );
  }, [accounts]);

  const filtered = useMemo(() => {
    let list = accounts;

    if (statusFilter) list = list.filter((acc) => acc.Status === statusFilter);
    if (platformFilter) list = list.filter((acc) => acc.Platform_Name === platformFilter);

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((acc) =>
        [
          acc.Account_Name,
          acc.Platform_Name,
          socialPlatformLabel(acc.Platform_Name),
          acc.Username,
          acc.Email_Used,
          acc.Contact_Number,
          acc.Profile_URL,
          acc.Business_URL,
          acc.Notes,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }

    return list;
  }, [accounts, search, statusFilter, platformFilter]);

  const resultFiltered = useMemo(() => {
    const q = resultKeyword.trim().toLowerCase();
    if (!q) return filtered;
    return filtered.filter((acc) =>
      [
        acc.Account_Name,
        acc.Platform_Name,
        socialPlatformLabel(acc.Platform_Name),
        acc.Username,
        acc.Email_Used,
        acc.Contact_Number,
        acc.Profile_URL,
        acc.Business_URL,
        acc.Notes,
        acc.Status,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [filtered, resultKeyword]);

  const hasActiveFilters = !!statusFilter || !!platformFilter || !!search;

  const clearFilters = () => {
    setStatusFilter("");
    setPlatformFilter("");
    setSearch("");
    setCurrentPage(1);
  };

  const sorted = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortKey];
    if (!accessor) return resultFiltered;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...resultFiltered].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [resultFiltered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const pageRows = sorted.slice(startIndex, startIndex + rowsPerPage);

  useEffect(() => {
    if (currentPage > 0) {
      localStorage.setItem(PORTAL_SOCIAL_CURRENT_PAGE_KEY, String(currentPage));
    }
  }, [currentPage]);

  const skipFilterPageResetRef = React.useRef(true);
  useEffect(() => {
    if (skipFilterPageResetRef.current) {
      skipFilterPageResetRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [search, resultKeyword, statusFilter, platformFilter]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  useEffect(() => {
    if (!createMode) return;
    setForm(EMPTY_FORM);
    setFormError("");
  }, [createMode]);

  const closeAddForm = () => navigate("/portal/social-accounts");

  const requestDeactivate = (acc) => {
    if (acc.Status === "Inactive") return;
    setDeactivateTarget(acc);
  };

  const closeDeactivateModal = () => {
    if (deactivatingId) return;
    setDeactivateTarget(null);
  };

  const confirmDeactivate = async () => {
    const acc = deactivateTarget;
    if (!acc) return;

    setDeactivatingId(acc.Account_Id);
    setError("");
    try {
      await axiosInstance.delete(`/client/social-accounts/${acc.Account_Id}`);
      await loadAccounts();
      setDeactivateTarget(null);
    } catch (err) {
      setError(err.response?.data?.message || "Could not deactivate social account.");
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.platformName.trim() ||
      !form.accountName.trim() ||
      !form.username.trim() ||
      !form.password.trim()
    ) {
      setFormError("Platform, account name, username, and password are required.");
      return;
    }
    const contactError = mobileValidationMessage(form.contactNumber, { label: "Contact number" });
    if (contactError) {
      setFormError(contactError);
      return;
    }

    setSubmitting(true);
    setFormError("");
    try {
      await axiosInstance.post("/client/social-accounts", {
        Platform_Name: form.platformName.trim(),
        Account_Name: form.accountName.trim(),
        Username: form.username.trim(),
        Password: form.password.trim(),
        Profile_URL: form.profileUrl.trim() || null,
        Business_URL: form.businessUrl.trim() || null,
        Email_Used: form.emailUsed.trim() || null,
        Contact_Number: form.contactNumber.trim() || null,
        Status: form.status || "Active",
        Notes: form.notes.trim() || null,
      });
      closeAddForm();
    } catch (err) {
      setFormError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Could not create social account."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cportal-page">
      {!createMode && (
        <>
      <div className="cportal-page-head">
        <h1>Social Accounts</h1>
      </div>

      <PortalAlert type="error" message={error} onClear={() => setError("")} />

      <PortalFiltersCollapsible
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={
          isCompactViewport ? "Search" : "Search account, username, email, contact"
        }
        searchAriaLabel="Search social accounts"
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        isCompactViewport={isCompactViewport}
        hasActiveFilters={hasActiveFilters}
        onClear={clearFilters}
        actions={
          <button
            type="button"
            className="cportal-btn cportal-btn-primary"
            onClick={() => navigate("/portal/social-accounts/new")}
          >
            Add account
          </button>
        }
      >
        <div className="cportal-filter-row">
          <select value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)}>
            <option value="">All platforms</option>
            {platformOptions.map((p) => (
              <option key={p} value={p}>
                {socialPlatformLabel(p)}
              </option>
            ))}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All status</option>
            {ACCOUNT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </PortalFiltersCollapsible>

      <div className="cportal-card">
        <PortalResultsHead
          heading="Social accounts"
          count={sorted.length}
          keyword={resultKeyword}
          onKeywordChange={setResultKeyword}
          ariaLabel="Keyword search social accounts"
        />

        {loading ? (
          <p className="cportal-muted">Loading…</p>
        ) : accounts.length === 0 ? (
          <p className="cportal-muted">No social accounts linked to your company yet.</p>
        ) : sorted.length === 0 ? (
          <p className="cportal-muted">No social accounts match your filters.</p>
        ) : (
          <>
          <div className="cportal-table-wrap co-desktop-table">
            <table className="cportal-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col.key} className="cportal-th-sortable">
                      <SupportSortableHeader
                        columnId={col.key}
                        label={col.label}
                        sortColumn={sortKey}
                        sortDirection={sortDir}
                        onSort={toggleSort}
                      />
                    </th>
                  ))}
                  <th className="cportal-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((acc) => (
                  <tr key={acc.Account_Id}>
                    <td>
                      <button
                        type="button"
                        className="cportal-table-link"
                        onClick={() => navigate(`/portal/social-accounts/${acc.Account_Id}`)}
                      >
                        {display(acc.Account_Name)}
                      </button>
                    </td>
                    <td>
                      {getSocialPlatformConfig(acc.Platform_Name) ? (
                        <SocialPlatformIcon platform={acc.Platform_Name} size={18} />
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{display(acc.Username)}</td>
                    <td>{linkOrDash(acc.Profile_URL)}</td>
                    <td>{display(acc.Email_Used)}</td>
                    <td>{display(acc.Contact_Number)}</td>
                    <td>
                      <span className={statusBadgeClass(acc.Status)}>{display(acc.Status)}</span>
                    </td>
                    <td className="cportal-td-actions">
                      <div className="cportal-re-actions">
                        <button
                          type="button"
                          className="re-lead-action re-lead-action--view"
                          onClick={() => navigate(`/portal/social-accounts/${acc.Account_Id}`)}
                          title="View account"
                          aria-label="View account"
                        >
                          <FaEye size={16} />
                        </button>
                        <button
                          type="button"
                          className="re-lead-action re-lead-action--edit"
                          onClick={() =>
                            navigate(`/portal/social-accounts/${acc.Account_Id}`, {
                              state: { edit: true },
                            })
                          }
                          title="Edit account"
                          aria-label="Edit account"
                        >
                          <FaEdit size={16} />
                        </button>
                        {canDelete && (
                          <button
                            type="button"
                            className="re-lead-action re-lead-action--delete"
                            onClick={() => requestDeactivate(acc)}
                            disabled={
                              acc.Status === "Inactive" ||
                              deactivatingId === acc.Account_Id
                            }
                            title="Delete account"
                            aria-label="Delete account"
                          >
                            <FaTrash size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ResultsMobileList
            emptyLabel="No social accounts match your filters."
            className="cportal-social-mobile"
          >
            {pageRows.map((acc) => {
              const id = acc.Account_Id;
              const status = acc.Status || "—";
              const statusTone =
                String(status).toLowerCase() === "active"
                  ? "success"
                  : String(status).toLowerCase() === "inactive"
                    ? "danger"
                    : "neutral";
              const platformLabel = socialPlatformLabel(acc.Platform_Name) || acc.Platform_Name;
              const profileUrl = String(acc.Profile_URL || "").trim();
              return (
                <ResultsMobileCard
                  key={id}
                  title={display(acc.Account_Name)}
                  subtitle={acc.Username || platformLabel || null}
                  meta={[
                    { label: status, tone: statusTone, status: true },
                    platformLabel ? { label: platformLabel, tone: "info" } : null,
                  ].filter(Boolean)}
                  fields={[
                    { label: "Username", value: acc.Username },
                    { label: "Email", value: acc.Email_Used },
                    { label: "Contact", value: acc.Contact_Number },
                    { label: "Profile", value: profileUrl || null },
                    { label: "Notes", value: acc.Notes },
                  ]}
                  onOpen={() => navigate(`/portal/social-accounts/${id}`)}
                  actions={
                    <div className="co-mcard__action-grid">
                      {profileUrl ? (
                        <a
                          className="co-maction"
                          href={profileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open profile"
                          aria-label="Open profile"
                        >
                          <FaExternalLinkAlt size={13} />
                          <span>Open</span>
                        </a>
                      ) : null}
                      <button
                        type="button"
                        className="co-maction"
                        title="View"
                        aria-label="View"
                        onClick={() => navigate(`/portal/social-accounts/${id}`)}
                      >
                        <FaEye size={14} />
                        <span>View</span>
                      </button>
                      <button
                        type="button"
                        className="co-maction"
                        title="Edit"
                        aria-label="Edit"
                        onClick={() =>
                          navigate(`/portal/social-accounts/${id}`, {
                            state: { edit: true },
                          })
                        }
                      >
                        <FaEdit size={14} />
                        <span>Edit</span>
                      </button>
                      {canDelete ? (
                        <button
                          type="button"
                          className="co-maction co-maction--danger"
                          title="Delete"
                          aria-label="Delete"
                          disabled={
                            acc.Status === "Inactive" || deactivatingId === id
                          }
                          onClick={() => requestDeactivate(acc)}
                        >
                          <FaTrash size={14} />
                          <span>Delete</span>
                        </button>
                      ) : null}
                    </div>
                  }
                />
              );
            })}
          </ResultsMobileList>
          </>
        )}

        {sorted.length > 0 && !loading && (
          <TablePagination
            currentPage={currentPage}
            totalItems={sorted.length}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            itemLabel="accounts"
            rowsPerPageSelectId="portal-social-accounts-rows-per-page"
          />
        )}
      </div>

      <PortalConfirmModal
        open={!!deactivateTarget}
        title="Deactivate Account"
        message={
          deactivateTarget ? (
            <p>
              Deactivate{" "}
              <strong>
                {deactivateTarget.Account_Name || deactivateTarget.Username || "this account"}
              </strong>
              ? Its status will be set to Inactive.
            </p>
          ) : null
        }
        confirmLabel="Yes"
        cancelLabel="No"
        confirming={!!deactivatingId}
        confirmingLabel="Deactivating…"
        onConfirm={confirmDeactivate}
        onClose={closeDeactivateModal}
      />

        </>
      )}

      {createMode && (
        <PortalFormPage
          title="Add Social Account"
          backLabel="Back to social accounts"
          onBack={closeAddForm}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel="Create account"
          submittingLabel="Creating…"
        >
              <PortalAlert type="error" message={formError} onClear={() => setFormError("")} />
              <div className="cportal-form-grid">
                <div className="cportal-field">
                  <label>
                    Platform <span className="cportal-required">*</span>
                  </label>
                  <PortalSelect
                    value={form.platformName}
                    onChange={(e) => setField("platformName", e.target.value)}
                    required
                  >
                    <option value="">Select platform</option>
                    {SOCIAL_PLATFORMS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </PortalSelect>
                </div>
                <div className="cportal-field">
                  <label>
                    Account name <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.accountName}
                    onChange={(e) => setField("accountName", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Username <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.username}
                    onChange={(e) => setField("username", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Password <span className="cportal-required">*</span>
                  </label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setField("password", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>Profile URL</label>
                  <input
                    type="url"
                    value={form.profileUrl}
                    onChange={(e) => setField("profileUrl", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>Business URL</label>
                  <input
                    type="url"
                    value={form.businessUrl}
                    onChange={(e) => setField("businessUrl", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>Email used</label>
                  <input
                    type="email"
                    value={form.emailUsed}
                    onChange={(e) => setField("emailUsed", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>Contact number</label>
                  <input
                    value={form.contactNumber}
                    onChange={(e) => setField("contactNumber", sanitizeMobileInput(e.target.value))}
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit number"
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Status <span className="cportal-required">*</span>
                  </label>
                  <PortalSelect value={form.status} onChange={(e) => setField("status", e.target.value)}>
                    {ACCOUNT_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </PortalSelect>
                </div>
                <div className="cportal-field cportal-field--full">
                  <label>Notes</label>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setField("notes", e.target.value)}
                  />
                </div>
              </div>
        </PortalFormPage>
      )}
    </div>
  );
}

export default TenantSocialAccounts;
